(() => {
  'use strict';
  const ready = new WeakSet();
  const safe = src => {
    if (typeof src !== 'string' || !src.trim()) return null;
    try {
      const url = new URL(src, location.origin);
      return url.protocol === 'https:' && [location.hostname, 'cdn.shopify.com'].includes(url.hostname) ? url.href : null;
    } catch { return null; }
  };
  const path = src => { try { return new URL(src, location.origin).pathname; } catch { return ''; } };
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function build(host, original, images, control, href, imageWidth) {
    images = [...new Set(images.map(safe).filter(Boolean))];
    if (images.length < 2 || !original) return;
    const track = document.createElement('div');
    track.className = 'sx-image-track';
    track.dataset.sxNativeGallery = '';
    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', original.alt || '');
    if (!href) track.tabIndex = 0;
    let index = Math.max(0, images.findIndex(src => path(src) === path(original.currentSrc || original.src)));
    let width = 0, frame, touch, blockedUntil = 0;
    const slides = images.map((src, i) => {
      const slide = document.createElement(href ? 'a' : 'div');
      slide.className = 'sx-image-slide';
      if (href) slide.href = href;
      const img = document.createElement('img');
      const url = new URL(src); url.searchParams.set('width', String(imageWidth));
      img.dataset.sxSrc = url.href;
      img.alt = original.alt || '';
      img.width = Number(original.getAttribute('width')) || 500;
      img.height = Number(original.getAttribute('height')) || 500;
      img.loading = i === index ? (original.getAttribute('loading') || 'eager') : 'lazy'; img.decoding = 'async'; img.draggable = false;
      if (i === index) {
        img.src = original.currentSrc || original.src || url.href;
        if (original.getAttribute('fetchpriority')) img.setAttribute('fetchpriority', original.getAttribute('fetchpriority'));
        if (original.getAttribute('srcset')) { img.srcset = original.getAttribute('srcset'); img.sizes = original.getAttribute('sizes') || '300px'; }
      }
      slide.append(img); track.append(slide); return slide;
    });
    function announce() {
      slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', String(i !== index));
        if (href) slide.tabIndex = i === index ? 0 : -1;
        if (Math.abs(i - index) <= 1) {
          const img = slide.querySelector('img');
          if (!img.hasAttribute('src')) img.src = img.dataset.sxSrc;
        }
      });
      const count = control?.querySelector('[data-sx-image-count]');
      if (count) count.textContent = (index + 1) + ' / ' + slides.length;
    }
    function goTo(next, smooth = true) {
      const old = index;
      index = (next + slides.length) % slides.length;
      announce();
      track.scrollTo({ left: index * track.clientWidth, behavior: smooth && !reduced() && Math.abs(old - index) <= 1 ? 'smooth' : 'auto' });
    }
    track.addEventListener('sx:image-step', event => { if ([-1, 1].includes(event.detail?.step)) goTo(index + event.detail.step); });
    track.addEventListener('keydown', event => {
      let next;
      if (event.key === 'ArrowRight') next = index + 1;
      else if (event.key === 'ArrowLeft') next = index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = slides.length - 1;
      else return;
      event.preventDefault(); goTo(next);
      if (href) slides[index].focus({ preventScroll: true });
    });
    track.addEventListener('scroll', () => {
      if (touch && Math.abs(track.scrollLeft - touch.left) > 6) { touch.moved = true; blockedUntil = performance.now() + 500; }
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (track.clientWidth > 0) {
          index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
          announce();
        }
      });
    }, { passive: true });
    track.addEventListener('pointerdown', event => {
      if (!['touch', 'pen'].includes(event.pointerType)) return;
      blockedUntil = 0;
      touch = { id: event.pointerId, x: event.clientX, y: event.clientY, left: track.scrollLeft, moved: false };
    }, { passive: true });
    track.addEventListener('pointermove', event => {
      if (!touch || touch.id !== event.pointerId) return;
      const dx = Math.abs(event.clientX - touch.x), dy = Math.abs(event.clientY - touch.y);
      if (dx > 12 && dx > dy * 1.2) touch.moved = true;
    }, { passive: true });
    const end = () => { if (touch?.moved) blockedUntil = performance.now() + 500; touch = null; };
    track.addEventListener('pointerup', end, { passive: true });
    track.addEventListener('touchend', end, { passive: true });
    track.addEventListener('touchcancel', end, { passive: true });
    track.addEventListener('click', event => {
      if (event.detail > 0 && (touch?.moved || performance.now() < blockedUntil)) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, true);
    if (href) host.append(track); else original.after(track);
    host.classList.add('sx-native-ready');
    original.setAttribute('aria-hidden', 'true');
    announce();
    const resize = new ResizeObserver(() => {
      if (track.clientWidth > 0 && track.clientWidth !== width) { width = track.clientWidth; goTo(index, false); }
    });
    resize.observe(track);
    if (!href) {
      const variant = new MutationObserver(() => {
        const next = images.findIndex(src => path(src) === path(original.src));
        if (next >= 0 && next !== index) goTo(next, false);
      });
      variant.observe(original, { attributes: true, attributeFilter: ['src'] });
    }
  }
  function initCard(card) {
    const media = card.querySelector('.card__media'), control = card.querySelector('[data-sx-swipe]'), link = card.querySelector('.card__heading a[href]');
    if (!media || !control || !link) return;
    const url = new URL(link.href, location.origin); if (url.origin !== location.origin) return;
    build(media, media.querySelector('img'), (control.dataset.images || '').split('|'), control, url.href, 600);
  }
  const nearby = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { nearby.unobserve(entry.target); initCard(entry.target); }
  }, { rootMargin: '300px' }) : null;
  function init(scope) {
    const cards = [...scope.querySelectorAll('.product-card-wrapper')];
    if (scope.matches?.('.product-card-wrapper')) cards.unshift(scope);
    cards.forEach(card => {
      if (ready.has(card)) return;
      if (!card.querySelector('[data-sx-swipe]')) return;
      ready.add(card); if (nearby) nearby.observe(card); else initCard(card);
    });
    const quicks = [...scope.querySelectorAll('.sx-quick-media')];
    if (scope.matches?.('.sx-quick-media')) quicks.unshift(scope);
    quicks.forEach(media => {
      if (ready.has(media) || !media.querySelector('.sx-image-controls')) return;
      try { const images = JSON.parse(media.dataset.sxImages || '[]'); ready.add(media); build(media, media.querySelector('img'), images, null, null, 700); } catch {}
    });
  }
  let pending = false;
  function schedule() { if (pending) return; pending = true; requestAnimationFrame(() => { pending = false; init(document); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true }); else init(document);
  new MutationObserver(records => { if (records.some(r => [...r.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('.product-card-wrapper,.sx-quick-media') || n.querySelector?.('.product-card-wrapper,.sx-quick-media'))))) schedule(); }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', schedule);
})();

